import { Component, Inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Observable } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
import {
  BaseDialogActionData,
  ButtonColor,
  CommonActionKey,
  EnumHelper,
  FormHelper,
  TransactionNextStatus,
  TransactionStatus,
  TransactionStatusLabel,
  TransactionStatusVerb,
  TransactionType,
  FailureReasonUsage
} from 'common';
import { CompetitorHintData } from '../../competitors/models/competitor.model';
import { readOnly } from '../../user/user-permission/user-permission.data';
import { UserPermissionService } from '../../user/user-permission/user-permission.service';
import {
  DrawRequirementData,
  DrawRequirementDisplayGroup,
  groupDrawRequirementLabels,
  toDrawRequirementDisplayGroups
} from '../transaction.model';
import { Note } from '../../shared/note/note.model';

@Component({
    selector: 'ifb-transaction-list-update-dialog',
    templateUrl: './transaction-list-update-dialog.component.html',
    styleUrls: ['../transaction-notes-section.scss'],
    standalone: false
})
export class TransactionListUpdateDialogComponent {
  actions: BaseDialogActionData[] = [
    { actionKey: CommonActionKey.Cancel, label: 'Cancel', buttonColor: ButtonColor.Default },
    { actionKey: CommonActionKey.Confirm, label: 'Confirm', buttonColor: ButtonColor.Primary, initialFocus: true },
  ];

  readonly TransactionStatus = TransactionStatus;
  readonly TransactionStatusLabel = TransactionStatusLabel;
  readonly TransactionStatusVerb = TransactionStatusVerb;
  readonly TransactionType = TransactionType;

  canReadCompetitors: boolean;
  isCompetitorsSpecifiable$: Observable<boolean>;
  showRejectReasons$: Observable<boolean>;
  form: UntypedFormGroup;
  public showWarning: boolean;
  public drawRequirementGroups: DrawRequirementDisplayGroup[] = [];
  readonly TransactionNextStatusOptions = EnumHelper.getNamesAndValues(TransactionNextStatus);
  readonly FailureReasonUsage = FailureReasonUsage;

  showNotes: boolean;
  existingNotes: { author: string; text: string; date: Date }[] = [];

  constructor(
    public dialogRef: MatDialogRef<TransactionListUpdateDialogComponent>,
    private formBuilder: UntypedFormBuilder,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly userPermissionService: UserPermissionService,
  ) {
    this.form = this.formBuilder.group({
      note: [undefined],
      nextStatus: [this.data.nextStatus],
      reasons: [this.data.reasons],
      competitors: [this.data.competitors],
    });

      this.showWarning = this.data.status === TransactionStatus.Approved
        && this.data.drawRequirements?.some((r: DrawRequirementData) =>
          !r.requirementCompleted && (r.required || r.advisory));

    if (this.showWarning) {
      this.setWarningFlags();
    }

    if (this.showWarning) {
      const noteControl = this.form.get('note');
      noteControl.setValidators(Validators.required);
    }

    this.userPermissionService
      .granted([readOnly('admin-competitors')])
      .subscribe((res) => (this.canReadCompetitors = res));

    this.isCompetitorsSpecifiable$ = this.form.valueChanges.pipe(
      startWith(this.form.value),
      map((form) => form?.reasons),
      map((selectedReasons) =>
        selectedReasons?.some(
          (selectedReason) =>
            this.data.failureReasons?.find((reason) => reason.code === selectedReason)?.allowToSpecifyCompetitors,
        ),
      ),
    );

      this.showRejectReasons$ = this.form.valueChanges.pipe(
        startWith(this.form.value),
        map(form => form?.nextStatus),
        map(nextStatus => {
          const status = this.data.status;
          if (status === TransactionStatus.Rejected)
            return true;
          if (status === TransactionStatus.OnHold)
            return nextStatus == TransactionStatus.Rejected;
          return false;
        })
      )

    this.showNotes = this.data.status === TransactionStatus.Approved;
    this.existingNotes = [...(this.data.notes ?? [])]
      .sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .map((n: any) => ({
        author: n.initiator?.fullName || n.initiator?.FullName || 'System',
        text: n.text,
        date: n.date,
      }));
    }

  save(): void {
    FormHelper.showInvalidFormFields(this.form);
    if (!this.form.valid) return;
    if (this.data.status == TransactionStatus.OnHold && this.form.value.reasons == null && this.form.value.note == null)
      return;

    const showCompetitors = this.form.value.reasons?.some(
      (reason) =>
        this.data.failureReasons?.find((failureReason) => failureReason.code == reason)?.allowToSpecifyCompetitors,
    );
    if (showCompetitors && !this.form.value.competitors?.length) return;
    this.dialogRef.close(this.form.value);
  }

  onCompetitorsChange(competitors: CompetitorHintData[]): void {
    this.form.get('competitors').setValue(competitors?.map((item) => item.id));
    this.form.get('competitors').markAsDirty();
    this.form.get('competitors').markAsTouched();
    this.form.get('competitors').updateValueAndValidity();
  }

  private setWarningFlags(): void {
    const grouped = groupDrawRequirementLabels(this.data.drawRequirements, {
      requestorState: this.data.requestorState,
      businessState: this.data.businessState
    });
    this.drawRequirementGroups = toDrawRequirementDisplayGroups(grouped, false);
  }

  static show(
    dialog: MatDialog,
    notes: Note[],
    loanNumber,
    status,
    nextStatus,
    failureReasons,
    previousStatus,
    transactionType,
    drawRequirements: DrawRequirementData[],
    requestorState: string,
    businessState: string,
    reasons: any[] = [],
    competitors: any[] = [],
  ): Observable<any> {
    return dialog
      .open(TransactionListUpdateDialogComponent, {
        width: '510px',
        data: {
          notes,
          loanNumber,
          status,
          nextStatus,
          failureReasons,
          previousStatus,
          transactionType,
          drawRequirements,
          requestorState,
          businessState,
          reasons,
          competitors,
        },
        closeOnNavigation: true,
      })
      .afterClosed();
  }

  onActionClicked(actionData: BaseDialogActionData): void {
    if (actionData.actionKey === CommonActionKey.Confirm) {
      this.save();
      return;
    }
    this.dialogRef.close(false);
  }
}
